import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  OnDestroy,
  OnInit
} from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { EventsBlotterGridStateKey } from '@approot/models';
import {
  AgGridService,
  EntityMapperService,
  UserStorageService
} from '@approot/services';
import { clone, SubscriptionSink } from '@approot/utilities';
import {
  AgGridEvent,
  ColDef,
  GridReadyEvent,
  SideBarDef
} from 'ag-grid-community';
import { auditTime, distinctUntilChanged, take } from 'rxjs/operators';
import { RunningState, sorter } from '../shared/utils';
import { applyColumnState } from './events-blotter.funcs';
import { EventsBlotterService } from './events-blotter.service';
import { EventsBlotterState } from './events-blotter.types';

@Component({
  selector: 'app-events-blotter',
  templateUrl: './events-blotter.component.html',
  styleUrls: ['./events-blotter.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class EventsBlotterComponent implements OnInit, OnDestroy {
  public sideBarDef: SideBarDef;
  public state: EventsBlotterState = {};
  public entities: string[] = [];
  public horizons: string[] = ['Today', '1W', '2W', 'All'];
  public runningState: RunningState = RunningState.Idle;
  public RunningState = RunningState;
  public readonly defaultColDef: ColDef = {
    sortable: true,
    enableRowGroup: true,
    resizable: true
  };
  public headers: any[] = [];
  public rowData: any[] = [];
  public readonly overlayNoRowsTemplate =
    '<span style="padding: 10px; border: 2px solid #444;">No events loaded</span>';
  public form: FormGroup = new FormGroup({
    entity: new FormControl<string>(null, Validators.required),
    horizon: new FormControl<string>(null, Validators.required)
  });

  private subsink = new SubscriptionSink();
  private agGridService = inject(AgGridService);
  private changeRef = inject(ChangeDetectorRef);
  private eventsBlotterService = inject(EventsBlotterService);
  private userStorageService = inject(UserStorageService);
  public entityMapperService = inject(EntityMapperService);
  public constructor() {
    this.sideBarDef = this.agGridService.sideBarDef;
    this.subsink.sink = this.form
      .get('entity')
      .valueChanges.pipe(distinctUntilChanged())
      .subscribe(v => this.eventsBlotterService.update({ selectedEntity: v }));
  }

  public ngOnInit(): void {
    // initial state.  Although we resubscribe to stateChanged in onGridReady, we
    // get it immediately (i.e., no `auditTime()`) so that the form is populated with
    // the correct entity, otherwise there's an annoying delay.
    this.eventsBlotterService.stateChanged.pipe(take(1)).subscribe({
      next: state => this.updateOnStateChange(state)
    });
  }

  public ngOnDestroy(): void {
    this.subsink.unsubscribe();
  }

  public onGridReady(params: GridReadyEvent) {
    this.subsink.sink = this.eventsBlotterService.stateChanged
      .pipe(
        auditTime(250) // Don't update too often
      )
      .subscribe({
        next: state => this.updateOnStateChange(state)
      });

    const columnState = this.userStorageService.getItem(
      EventsBlotterGridStateKey
    );
    // sometimes we get a string of 'undefined' from local storage so ignore that
    if (columnState && columnState !== 'undefined') {
      applyColumnState(params.columnApi, columnState);
    }
  }

  public gridStateChanged(event: AgGridEvent) {
    const allState = event.columnApi.getColumnState();
    if (allState) {
      this.userStorageService.setItem(EventsBlotterGridStateKey, allState);
    } else {
      this.userStorageService.removeItem(EventsBlotterGridStateKey);
    }
  }

  public runReport() {
    const entity = this.form.getRawValue().entity as string;
    const horizon = this.form.getRawValue().horizon as string;
    if (entity == null || horizon == null) {
      return;
    }
    this.eventsBlotterService.runReport(entity, horizon);
  }

  public cancelReport() {
    this.eventsBlotterService.cancelReport();
  }

  private updateOnStateChange(state: EventsBlotterState) {
    this.state = state;
    // We don't want to update the grid if the data unchanged
    if (this.rowData.length !== state.rows.length) {
      this.rowData = clone(state.rows);

      this.rowData.forEach(row => {
        ['counterPartyA', 'counterPartyB'].forEach(col => {
          if (row[col]) {
            row[col] = this.entityMapperService.getDisplayEntity(row[col]);
          }
        });
      });
    }
    if (state.entities && this.entities?.length !== state.entities.length) {
      this.entities = [...state.entities];
      this.entities.sort(sorter);
    }
    if (this.headers.length !== state.headers.length) {
      this.headers = state.headers;
    }
    this.form
      .get('entity')
      .setValue(state.selectedEntity, { emitEvent: false });
    this.changeRef.markForCheck();
  }
}
